import { Star, Zap, Shield, Clock, Award, Wallet, Coins } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SiteLayout from "@/components/SiteLayout";
import SectionTitle from "@/components/SectionTitle";
import { useAuthModals } from "@/contexts/AuthModalsContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { platforms } from "@/data/products";
import { getTranslatedPlatformName } from "@/i18n/productTranslations";
import { seoData, generateWebsiteSchema, generateOrganizationSchema } from "@/lib/seo";
import { getPlatformTheme } from "@/lib/platformColors";
import heroImg1 from "@/assets/hero1.webp";
import heroImg2 from "@/assets/hero2.webp";

const featuredReviews = [
  { name: "אורי כהן", location: "תל אביב, ישראל", date: "14:22:10 03-25-2026", rating: 5, text: "שירות מטורף! קיבלתי 5000 עוקבים באינסטגרם תוך שעות. ממליץ בחום 🔥", platform: "instagram" },
  { name: "נועה לוי", location: "חיפה, ישראל", date: "09:15:33 03-20-2026", rating: 5, text: "השתמשתי כבר כמה פעמים, תמיד מקבלת תוצאות מעולות. האתר הכי אמין שיש", platform: "tiktok" },
  { name: "יוסי אברהם", location: "ירושלים, ישראל", date: "18:44:02 03-18-2026", rating: 5, text: "קניתי צפיות ליוטיוב והסרטון שלי עלה באלגוריתם. שווה כל שקל!", platform: "youtube" },
  { name: "שירה מזרחי", location: "ראשון לציון, ישראל", date: "11:30:45 03-15-2026", rating: 5, text: "מהיר, אמין, ומחירים הוגנים. מה עוד צריך? 💯", platform: "telegram" },
  { name: "Carlos", location: "Amsterdam", date: "00:52:17 10-23-2024", rating: 5, text: "Best service I've ever used, highly recommended!", platform: "twitter" },
  { name: "Martin", location: "England", date: "22:38:34 05-20-2024", rating: 5, text: "Excellent service and affordable, been using it for years!", platform: "facebook" },
];

const StarRating = ({ rating }: { rating: number }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-4 h-4 ${i <= rating ? "text-star-gold fill-star-gold" : "text-muted-foreground"}`} style={i <= rating ? { filter: 'drop-shadow(0 0 4px hsl(45 100% 51% / 0.5))' } : {}} />
    ))}
  </div>
);

const Index = () => {
  const { openLogin, openSignup } = useAuthModals();
  const { user } = useAuth();
  const { t, lang } = useLanguage();

  const seoBlocks = [
    { title: t.seoBlock1Title, text: t.seoBlock1Text, platform: "instagram" },
    { title: t.seoBlock2Title, text: t.seoBlock2Text, platform: "youtube" },
    { title: t.seoBlock3Title, text: t.seoBlock3Text, platform: "youtube" },
    { title: t.seoBlock4Title, text: t.seoBlock4Text, platform: "twitter" },
    { title: t.seoBlock5Title, text: t.seoBlock5Text, platform: "facebook" },
  ];

  const features = [
    { num: "01", title: t.feature1Title, desc: t.feature1Desc, img: "/images/home/feature-quality.webp", color: "--neon-cyan" },
    { num: "02", title: t.feature2Title, desc: t.feature2Desc, img: "/images/home/feature-reliability.webp", color: "--neon-purple" },
    { num: "03", title: t.feature3Title, desc: t.feature3Desc, img: "/images/home/feature-support.webp", color: "--neon-pink" },
    { num: "04", title: t.feature4Title, desc: t.feature4Desc, img: "/images/home/feature-experience.webp", color: "--neon-green" },
  ];

  return (
    <SiteLayout>
      <Helmet>
        <title>{seoData.home.title}</title>
        <meta name="description" content={seoData.home.description} />
        <link rel="canonical" href={seoData.home.canonical} />
        <meta property="og:title" content={seoData.home.title} />
        <meta property="og:description" content={seoData.home.description} />
        <meta property="og:url" content={seoData.home.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://mehubarim.co.il/images/home/mehubarim-logo.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.home.title} />
        <meta name="twitter:description" content={seoData.home.description} />
        <meta name="twitter:image" content="https://mehubarim.co.il/images/home/mehubarim-logo.webp" />
        <script type="application/ld+json">{JSON.stringify(generateWebsiteSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(generateOrganizationSchema())}</script>
      </Helmet>

      {/* Hero Section 1 */}
      <section className="max-w-6xl mx-auto px-4 py-12 md:py-20 relative">
        {/* Ambient glow orbs */}
        <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full opacity-20 pointer-events-none" style={{ background: 'radial-gradient(circle, hsl(174 90% 50% / 0.3), transparent 70%)', filter: 'blur(60px)' }} />
        <div className="absolute bottom-0 left-1/4 w-80 h-80 rounded-full opacity-15 pointer-events-none" style={{ background: 'radial-gradient(circle, hsl(280 80% 55% / 0.3), transparent 70%)', filter: 'blur(50px)' }} />
        <div className="flex flex-col md:flex-row-reverse items-center gap-8 md:gap-12">
          <div className="flex-1 space-y-6 animate-fade-in-up">
            <div>
              <p className="text-2xl md:text-3xl font-bold text-foreground font-display tracking-wide">
                {t.welcomeTo}<span className="neon-text-cyan">{t.brandName}</span>
              </p>
              <div className="accent-bars mt-3">
                <div className="accent-bar-long" />
                <div className="accent-bar-short" />
              </div>
            </div>
            <p className="text-muted-foreground leading-relaxed text-sm md:text-base max-w-lg">
              {t.heroDesc}
            </p>
            {!user && (
              <div className="flex gap-4 flex-wrap">
                <button onClick={openLogin} className="neon-btn px-8 py-3 text-base">
                  <span className="flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    {t.login}
                  </span>
                </button>
                <button onClick={openSignup} className="neon-btn-outline px-8 py-3 text-base">
                  ○ {t.signup}
                </button>
              </div>
            )}
          </div>
          <div className="flex-1 flex justify-center">
            <div className="relative animate-float">
              {/* Floating platform icons bursting from image */}
              {[
                { src: "/images/platforms/instagram-official.webp", top: "-30px", left: "10px", delay: "0s" },
                { src: "/images/platforms/youtube-official.webp", top: "-20px", right: "-15px", delay: "0.4s" },
                { src: "/images/platforms/tiktok-new.webp", top: "30%", left: "-35px", delay: "0.8s" },
                { src: "/images/platforms/twitter-official.webp", top: "25%", right: "-30px", delay: "1.2s" },
                { src: "/images/platforms/facebook-official.webp", bottom: "30%", left: "-30px", delay: "1.6s" },
                { src: "/images/platforms/telegram-new.webp", bottom: "25%", right: "-35px", delay: "0.6s" },
                { src: "/images/platforms/spotify-official.webp", bottom: "-25px", left: "20px", delay: "1s" },
                { src: "/images/platforms/twitch-official.webp", bottom: "-20px", right: "15px", delay: "1.4s" },
              ].map((icon, i) => (
                <div
                  key={i}
                  className="absolute z-30 w-12 h-12 md:w-14 md:h-14 rounded-full bg-card/90 border border-primary/40 p-2 shadow-[0_0_25px_hsl(174_90%_50%/0.4),0_0_50px_hsl(174_90%_50%/0.15)]"
                  style={{
                    top: icon.top, bottom: (icon as any).bottom, left: icon.left, right: (icon as any).right,
                    animation: `icon-burst ${2.5 + i * 0.3}s ease-in-out ${icon.delay} infinite`,
                  }}
                >
                  <img src={icon.src} alt="" className="w-full h-full object-contain drop-shadow-[0_0_6px_hsl(174_90%_50%/0.4)]" />
                </div>
              ))}
              <div className="w-72 h-72 md:w-96 md:h-96 rounded-full overflow-hidden border-2 border-primary/50 shadow-[0_0_40px_hsl(174_90%_50%/0.35),0_0_80px_hsl(174_90%_50%/0.15)] relative z-10">
                <img src={heroImg1} alt={t.brandName} className="w-full h-full object-cover" width={800} height={600} loading="eager" fetchPriority="high" />
              </div>
              <div className="absolute inset-0 -z-0 rounded-full" style={{ background: 'radial-gradient(ellipse at center, hsl(174 90% 50% / 0.25), transparent 70%)', filter: 'blur(50px)' }} />
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section 2 */}
      <section className="max-w-6xl mx-auto px-4 py-12 md:py-16 relative">
        <div className="absolute inset-0 opacity-40" style={{ background: 'radial-gradient(ellipse at 30% 50%, hsl(280 80% 55% / 0.12), transparent 60%)' }} />
        <div className="absolute top-1/3 right-0 w-72 h-72 rounded-full opacity-20 pointer-events-none" style={{ background: 'radial-gradient(circle, hsl(320 85% 55% / 0.25), transparent 70%)', filter: 'blur(50px)' }} />
        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 relative">
          <div className="flex-1 flex justify-center">
            <div className="relative">
              {/* Floating platform icons */}
              {[
                { src: "/images/platforms/discord-official.webp", top: "-30px", left: "15px", delay: "0.2s" },
                { src: "/images/platforms/linkedin-official.webp", top: "-20px", right: "-10px", delay: "0.7s" },
                { src: "/images/platforms/soundcloud-official.webp", top: "28%", left: "-35px", delay: "1.1s" },
                { src: "/images/platforms/reddit-official.webp", top: "22%", right: "-30px", delay: "0.3s" },
                { src: "/images/platforms/clubhouse-official.webp", bottom: "28%", left: "-28px", delay: "1.5s" },
                { src: "/images/platforms/instagram-official.webp", bottom: "22%", right: "-32px", delay: "0.9s" },
                { src: "/images/platforms/youtube-official.webp", bottom: "-22px", left: "20px", delay: "1.3s" },
                { src: "/images/platforms/telegram-new.webp", bottom: "-18px", right: "18px", delay: "0.5s" },
              ].map((icon, i) => (
                <div
                  key={i}
                  className="absolute z-30 w-12 h-12 md:w-14 md:h-14 rounded-full bg-card/90 border border-purple-500/40 p-2 shadow-[0_0_25px_hsl(280_80%_55%/0.4),0_0_50px_hsl(280_80%_55%/0.15)]"
                  style={{
                    top: icon.top, bottom: (icon as any).bottom, left: icon.left, right: (icon as any).right,
                    animation: `icon-burst ${2.5 + i * 0.3}s ease-in-out ${icon.delay} infinite`,
                  }}
                >
                  <img src={icon.src} alt="" className="w-full h-full object-contain drop-shadow-[0_0_6px_hsl(280_80%_55%/0.4)]" />
                </div>
              ))}
              <div className="w-72 h-72 md:w-96 md:h-96 rounded-full overflow-hidden border-2 border-purple-500/50 shadow-[0_0_40px_hsl(280_80%_55%/0.35),0_0_80px_hsl(280_80%_55%/0.15)] relative z-10">
                <img src={heroImg2} alt={t.smmLabel} className="w-full h-full object-cover" width={800} height={600} loading="eager" />
              </div>
              <div className="absolute inset-0 -z-0 rounded-full" style={{ background: 'radial-gradient(ellipse at center, hsl(280 80% 55% / 0.25), transparent 70%)', filter: 'blur(50px)' }} />
            </div>
          </div>
          <div className="flex-1 space-y-6">
            <p className="text-sm font-semibold neon-text-purple tracking-widest uppercase">{t.smmLabel}</p>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground leading-tight font-display">
              {t.heroTitle}
            </h1>
            <div className="accent-bars">
              <div className="accent-bar-long" />
              <div className="accent-bar-short" />
            </div>
            <p className="text-muted-foreground leading-relaxed text-sm md:text-base">{t.heroDesc2}</p>
            <p className="font-bold text-foreground text-sm md:text-base">{t.heroExperience}</p>
            <button onClick={openLogin} className="neon-btn-outline px-8 py-3 text-base">
              {t.askQuestion}
            </button>
          </div>
        </div>
      </section>

      {/* Credits CTA Banner */}
      <section className="max-w-6xl mx-auto px-4 py-6 cv-auto">
        <Link
          to="/topup"
          className="neon-card p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 group"
          style={{ borderColor: 'hsl(var(--neon-cyan) / 0.3)', background: 'linear-gradient(135deg, hsl(var(--neon-cyan) / 0.05), hsl(var(--neon-purple) / 0.05), hsl(var(--neon-pink) / 0.03))' }}
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 animate-float" style={{ background: 'hsl(var(--neon-cyan) / 0.1)', boxShadow: '0 0 25px hsl(var(--neon-cyan) / 0.3), 0 0 50px hsl(var(--neon-cyan) / 0.1)' }}>
              <Wallet className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground font-display neon-text-cyan">{t.loadAndStart}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t.creditInfo}</p>
            </div>
          </div>
          <span className="neon-btn px-8 py-3 text-base flex items-center gap-2 group-hover:neon-glow-cyan-strong flex-shrink-0">
            <Coins className="w-5 h-5" /> {t.loadCredits}
          </span>
        </Link>
      </section>

      {/* Platforms Grid */}
      <section className="py-12 relative cv-auto">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-15 pointer-events-none" style={{ background: 'radial-gradient(circle, hsl(174 90% 50% / 0.2), transparent 70%)', filter: 'blur(80px)' }} />
        <SectionTitle label="mehubarim.co.il" title={t.ourPlatforms} />
        <div className="max-w-6xl mx-auto px-4 mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
          {platforms.map((platform, i) => (
            <Link
              key={platform.id}
              to={`/${platform.slug}`}
              className="neon-card p-6 flex flex-col items-center gap-4 group transition-all duration-300"
              style={{
                animationDelay: `${i * 0.05}s`,
                borderColor: getPlatformTheme(platform.slug).borderColor,
                boxShadow: `0 0 15px ${getPlatformTheme(platform.slug).glow.replace('0.4', '0.15')}`,
                background: `linear-gradient(135deg, ${getPlatformTheme(platform.slug).badgeBg}, transparent)`,
              }}
              onMouseEnter={(e) => {
                const theme = getPlatformTheme(platform.slug);
                e.currentTarget.style.borderColor = theme.primary;
                e.currentTarget.style.boxShadow = `0 0 25px ${theme.glow}, 0 0 50px ${theme.glow.replace('0.4', '0.15')}`;
                e.currentTarget.style.background = `linear-gradient(135deg, ${theme.badgeBg.replace('0.1', '0.15')}, transparent)`;
              }}
              onMouseLeave={(e) => {
                const theme = getPlatformTheme(platform.slug);
                e.currentTarget.style.borderColor = theme.borderColor;
                e.currentTarget.style.boxShadow = `0 0 15px ${theme.glow.replace('0.4', '0.15')}`;
                e.currentTarget.style.background = `linear-gradient(135deg, ${theme.badgeBg}, transparent)`;
              }}
            >
              <img src={platform.icon} alt={getTranslatedPlatformName(platform.slug, lang)} className="w-28 h-28 object-contain group-hover:scale-110 transition-transform duration-300" style={{ filter: `drop-shadow(0 0 8px ${getPlatformTheme(platform.slug).glow})` }} />
              <span className="font-semibold text-base font-display tracking-wide" style={{ color: getPlatformTheme(platform.slug).primary }}>{getTranslatedPlatformName(platform.slug, lang)}</span>
              <span className="text-sm text-muted-foreground">{platform.products.length} {t.products}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 relative cv-auto">
        <div className="absolute inset-0 opacity-25" style={{ background: 'radial-gradient(ellipse at 70% 50%, hsl(174 90% 50% / 0.12), transparent 50%)' }} />
        <div className="absolute top-0 left-1/4 w-80 h-80 rounded-full opacity-15 pointer-events-none" style={{ background: 'radial-gradient(circle, hsl(320 85% 55% / 0.2), transparent 70%)', filter: 'blur(60px)' }} />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full opacity-15 pointer-events-none" style={{ background: 'radial-gradient(circle, hsl(150 80% 50% / 0.2), transparent 70%)', filter: 'blur(50px)' }} />
        <SectionTitle label="mehubarim.co.il" title={t.whyChooseUs} />
        <div className="max-w-6xl mx-auto px-4 mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {features.map((feature, i) => (
            <div
              key={feature.num}
              className="neon-card p-6 text-center space-y-4 animate-fade-in-up transition-all duration-300"
              style={{
                animationDelay: `${i * 0.1}s`,
                borderColor: `hsl(var(${feature.color}) / 0.3)`,
                boxShadow: `0 0 15px hsl(var(${feature.color}) / 0.1)`,
                background: `linear-gradient(135deg, hsl(var(${feature.color}) / 0.05), transparent)`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = `hsl(var(${feature.color}) / 0.6)`;
                e.currentTarget.style.boxShadow = `0 0 25px hsl(var(${feature.color}) / 0.25), 0 0 50px hsl(var(${feature.color}) / 0.1)`;
                e.currentTarget.style.background = `linear-gradient(135deg, hsl(var(${feature.color}) / 0.1), transparent)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = `hsl(var(${feature.color}) / 0.3)`;
                e.currentTarget.style.boxShadow = `0 0 15px hsl(var(${feature.color}) / 0.1)`;
                e.currentTarget.style.background = `linear-gradient(135deg, hsl(var(${feature.color}) / 0.05), transparent)`;
              }}
            >
              <div className="w-24 h-24 mx-auto">
                <img src={feature.img} alt={feature.title} className="w-full h-full object-contain" loading="lazy" />
              </div>
              <h3 className="font-bold text-foreground font-display tracking-wide">{feature.title}</h3>
              <div className="accent-bars justify-center">
                <div className="accent-bar-long" />
                <div className="accent-bar-short" />
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{feature.desc}</p>
              <div
                className="w-12 h-12 mx-auto rounded-full flex items-center justify-center font-display font-bold text-lg tabular-nums"
                style={{
                  background: `hsl(var(${feature.color}) / 0.15)`,
                  color: `hsl(var(${feature.color}))`,
                  boxShadow: `0 0 15px hsl(var(${feature.color}) / 0.3), 0 0 30px hsl(var(${feature.color}) / 0.1)`,
                  textShadow: `0 0 8px hsl(var(${feature.color}) / 0.5)`,
                }}
              >
                {feature.num}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SEO Content */}
      <section className="max-w-4xl mx-auto px-4 py-12 space-y-8 cv-auto">
        {seoBlocks.map((block) => {
          const theme = getPlatformTheme(block.platform);
          return (
            <div
              key={block.title}
              className="neon-card p-6 transition-all duration-300"
              style={{
                borderColor: theme.borderColor,
                boxShadow: `0 0 15px ${theme.glow.replace('0.4', '0.12')}`,
                background: `linear-gradient(135deg, ${theme.badgeBg}, transparent)`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = theme.primary;
                e.currentTarget.style.boxShadow = `0 0 25px ${theme.glow}, 0 0 50px ${theme.glow.replace('0.4', '0.15')}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme.borderColor;
                e.currentTarget.style.boxShadow = `0 0 15px ${theme.glow.replace('0.4', '0.12')}`;
              }}
            >
              <h2 className="text-xl md:text-2xl font-bold mb-3 font-display" style={{ color: theme.primary, textShadow: theme.textShadow }}>{block.title}</h2>
              <p className="text-muted-foreground leading-relaxed text-sm md:text-base">{block.text}</p>
            </div>
          );
        })}
      </section>

      {/* Customer Reviews */}
      <section className="py-16 relative cv-auto">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-15 pointer-events-none" style={{ background: 'radial-gradient(circle, hsl(45 100% 51% / 0.15), transparent 70%)', filter: 'blur(70px)' }} />
        <SectionTitle label="mehubarim.co.il" title={t.customerReviews} />
        <div className="max-w-5xl mx-auto px-4 mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredReviews.map((review) => {
            const theme = getPlatformTheme(review.platform);
            return (
              <div
                key={review.name}
                className="flex-1 neon-card p-6 space-y-3 transition-all duration-300"
                style={{
                  borderColor: theme.borderColor,
                  boxShadow: `0 0 12px ${theme.glow.replace('0.4', '0.1')}`,
                  background: `linear-gradient(135deg, ${theme.badgeBg}, transparent)`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 25px ${theme.glow}, 0 0 50px ${theme.glow.replace('0.4', '0.12')}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = theme.borderColor;
                  e.currentTarget.style.boxShadow = `0 0 12px ${theme.glow.replace('0.4', '0.1')}`;
                }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0" style={{ background: theme.badgeBg, boxShadow: `0 0 15px ${theme.glow.replace('0.4', '0.2')}`, color: theme.primary, textShadow: theme.textShadow }}>
                    {review.name[0]}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">📅 {review.date}</p>
                    <p className="font-bold text-foreground">{review.name}</p>
                    <p className="text-xs text-muted-foreground">📍 {review.location}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{t.rating}: {review.rating}</span>
                      <StarRating rating={review.rating} />
                    </div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{review.text}</p>
                <Link to="/reviews" className="text-sm hover:underline font-medium inline-flex items-center gap-1" style={{ color: theme.primary, textShadow: theme.textShadow }}>
                  {t.moreDetails}
                </Link>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-10">
          <Link to="/reviews" className="inline-block neon-btn px-10 py-3 font-semibold text-base">
            {t.showAllReviews}
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
};

export default Index;
